// F3-Debug. Alles, was Spieler selbst herausfinden sollen (Fahrplan), steht
// nur hier — niemals im HUD (Konzept §4).

import { formatClock, formatDuration } from '../../shared/worldtime.js';
import { biomeAt, featureAt, projectToTrack } from '../../shared/track.js';
import { scheduleTableAll } from '../../shared/schedule.js';
import { DEBUG } from '../../shared/constants.js';

const REFRESH_S = 0.2; // max 5 Hz — DOM nicht pro Frame füttern

export class DebugSystem {
  async init(ctx) {
    this.el = document.getElementById('debug');
    this.visible = false;
    this._acc = 0;
    this._text = '';
    if (this.el) this.el.classList.add('hidden');
  }

  update(dt, ctx) {
    const input = ctx.input;
    if (input?.justPressed('debug')) {
      this.visible = !this.visible;
      if (this.el) this.el.classList.toggle('hidden', !this.visible);
    }
    if (!this.visible || !this.el) return;

    this._acc += dt;
    if (this._acc < REFRESH_S) return;
    this._acc = 0;

    const text = this._build(ctx);
    if (text !== this._text) {
      this._text = text;
      this.el.textContent = text;
    }
  }

  _build(ctx) {
    const eng = ctx.engine;
    const st = eng?.stats ?? {};
    const ps = ctx.playerState;
    const train = ctx.train;
    const weather = ctx.weather ?? {};
    const net = ctx.net;
    const paint = ctx.systems?.paint;

    const pos = ps?.pos;
    const s = ps?.trackS;
    let lateral = ps?.lateral;
    let biome = null;
    let feature = null;
    if (typeof s === 'number') {
      biome = biomeAt(s);
      feature = featureAt(s);
    } else if (pos) {
      const proj = projectToTrack(pos.x, pos.z, ctx.trackHint ?? -1);
      lateral = proj.lateral;
      biome = biomeAt(proj.s);
      feature = featureAt(proj.s);
    }

    const trains = Array.isArray(ctx.trains) && ctx.trains.length
      ? ctx.trains
      : (train ? [train] : []);
    const lines = [];

    lines.push(`fps ${st.fps ?? '—'}  frame ${((st.frameMs ?? 0)).toFixed(1)} ms`);
    lines.push(`draw ${st.drawCalls ?? '—'}  tris ${st.triangles ?? '—'}`);
    lines.push('');

    if (pos) {
      lines.push(`pos ${pos.x.toFixed(1)} ${pos.y.toFixed(1)} ${pos.z.toFixed(1)}`);
    }
    lines.push(
      `s ${(s ?? 0).toFixed(1)}  lat ${(lateral ?? 0).toFixed(2)}  `
      + `biom ${biome?.id ?? '—'}  feat ${feature?.type ?? '—'}`,
    );
    lines.push(
      `mode ${ps?.mode ?? '—'}  car ${ps?.onCarId ?? '—'}  `
      + `crouch ${ps?.crouching ? 'y' : 'n'}`,
    );
    lines.push(
      `warmth ${ps?.warmth?.toFixed?.(1) ?? '—'}  `
      + `wet ${ps?.wetness?.toFixed?.(1) ?? '—'}`,
    );
    // Godmode nur hier markieren — nicht im HUD (Konzept §4/§8).
    // Sonst hält man später einen Fehler für ein Feature.
    if (ps?.mode === 'fly') {
      lines.push('GODMODE fliegen  [G] zurück  Shift=turbo');
    }
    if (ps?.flashlightOn) {
      lines.push('taschenlampe an  [L]');
    }
    lines.push('');

    // Alle Züge (ID, Position, Zustand, Strecke) — nur F3, nie HUD (§4, §8).
    if (trains.length) {
      for (const tr of trains) {
        const id = tr.id ?? (tr === train ? 'local' : '?');
        const kmh = ((tr.realSpeed ?? 0) * 3.6).toFixed(1);
        lines.push(
          `zug[${id}] s=${(tr.headS ?? 0).toFixed(1)}  ${kmh} km/h  `
          + `dir ${tr.direction ?? 0}  ${tr.state ?? '—'}  `
          + `st ${tr.station ?? '—'}`,
        );
        lines.push(
          `  branch ${tr.branch ?? 'main'}  run ${tr.runKind ?? 'main'}  `
          + `jolt ${(tr.jolt ?? 0).toFixed(2)}`
          + (tr.boardable === false ? '  (no-board)' : ''),
        );
      }
    } else {
      lines.push('zug —');
    }
    lines.push('');

    // Fahrplan nur hier (Konzept §4: Spieler sollen ihn selbst knacken).
    const ws = ctx.worldSeconds ?? 0;
    lines.push(`zeit ${formatClock(ws, true)}`);
    try {
      const rows = scheduleTableAll(ws, 4);
      for (const leg of rows.slice(0, 12)) {
        const when = formatClock(leg.t0, false);
        const dur = formatDuration(leg.t1 - leg.t0);
        const br = leg.branch ?? 'main';
        const rk = leg.runKind ? ` [${leg.runKind}]` : '';
        const id = leg.trainId ?? 'local';
        if (leg.kind === 'stand') {
          lines.push(`  ${when}  ${id} halt ${leg.station} (${br})${rk}  ${dur}`);
        } else {
          const dir = leg.direction > 0 ? '+' : '−';
          const route = leg.route && leg.route !== 'main' ? ` ${leg.route}` : '';
          lines.push(`  ${when}  ${id} fahrt ${dir} ${br}${route}${rk}  ${dur}`);
        }
      }
    } catch (err) {
      lines.push(`  fahrplan-fehler: ${err.message}`);
    }
    lines.push('');

    if (net?.stats) {
      const ns = net.stats();
      lines.push(
        `net ${ns.connected ? 'on' : 'off'}  id ${ns.id ?? '—'}  `
        + `rtt ${ns.rtt}  peers ${ns.peers}`,
      );
      lines.push(`  in ${ns.kbIn} kB  out ${ns.kbOut} kB  off ${ns.offset} ms`);
    }

    lines.push(
      `wetter rain ${(weather.rain ?? 0).toFixed(2)}  `
      + `fog ${(weather.fog ?? 0).toFixed(2)}  `
      + `wind ${(weather.wind ?? 0).toFixed(2)}`,
    );

    if (paint) {
      const live = paint.liveTextures ?? paint.textureCount ?? paint.activeTextures;
      if (live != null) lines.push(`paint textures ${live}`);
    }

    if (!DEBUG) lines.push('(DEBUG-Flag aus)');

    return lines.join('\n');
  }

  dispose() {}
}
